import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ButtonDelete, ButtonSetPin } from "@/components/ui/button";
import { formatDate, getInitials, getProfilePictureUrl } from "@/lib/utils";
import DataTable, { Column } from "@/components/ui/DataTable";
import { useAppSelector } from "@/redux/features/hooks";
import {
  useGetRestaurantManagersQuery,
  useRemoveRestaurantManagerMutation,
} from "@/redux/services/restaurant/restaurant.api";
import { RestaurantManager } from "@/redux/services/restaurant/restaurant.type";
import { Roles, User } from "@/redux/services/user/user.type";
import toast from "react-hot-toast";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { Trash2 } from "lucide-react";
import SetUserPinModal from "../modals/SetUserPinModal";

const ListeRestaurantManagers = () => {
  const { currentRestaurant, currentUser } = useAppSelector((s) => s.user);
  const isOwner = currentUser?.roles?.includes(Roles.owner);
  const hasRestaurant = !!currentRestaurant?.id;

  const { data, isLoading } = useGetRestaurantManagersQuery(
    currentRestaurant?.id as string,
    { skip: !hasRestaurant }
  );
  const [removeManager] = useRemoveRestaurantManagerMutation();
  const confirm = useConfirm();
  const [pinTarget, setPinTarget] = useState<User | null>(null);

  const isPrimaryManager =
    !isOwner &&
    data?.some((m) => m.isPrimary && m.manager.id === currentUser?.id);
  // Removing a manager (and resetting another manager's PIN) is reserved to
  // the primary manager and the owner — the API refuses it for secondary
  // managers.
  const canManage = isOwner || (isPrimaryManager ?? false);
  const canSetManagerPin = canManage;

  const handleRemove = async (item: RestaurantManager) => {
    if (item.isPrimary) return;
    const ok = await confirm({
      title: "Retirer ce manager ?",
      description: `Voulez-vous retirer ${item.manager.fullName} ?`,
      confirmText: "Oui, retirer",
      cancelText: "Annuler",
      variant: "destructive",
      icon: <Trash2 className="h-6 w-6" />,
    });
    if (!ok) return;
    const res = await removeManager({
      restaurantId: currentRestaurant?.id as string,
      // The route targets the RestaurantManager record, not the user.
      managerId: item.id,
    });
    if ("data" in res || !("error" in res)) {
      toast.success("Manager retiré avec succès");
    } else {
      toast.error("Une erreur est survenue");
    }
  };

  if (!hasRestaurant) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        <p>Sélectionnez un restaurant pour voir les managers</p>
      </div>
    );
  }

  const columns: Column[] = [
    {
      key: "fullName",
      label: "Nom complet",
      formatter: (_value: any, row: any) => {
        const item = row as RestaurantManager;
        return (
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarImage
                src={getProfilePictureUrl(item?.manager?.image?.url)}
                alt="avatar"
              />
              <AvatarFallback className="text-xs">
                {getInitials(item?.manager?.fullName)}
              </AvatarFallback>
            </Avatar>
            <span className="font-medium">{item?.manager?.fullName}</span>
          </div>
        );
      },
    },
    {
      key: "email",
      label: "Email",
      formatter: (_value: any, row: any) => (row as RestaurantManager)?.manager?.email || "-",
    },
    {
      key: "phone",
      label: "Téléphone",
      formatter: (_value: any, row: any) => (row as RestaurantManager)?.manager?.phone || "-",
    },
    {
      key: "isPrimary",
      label: "Statut",
      formatter: (_value: any, row: any) => {
        const item = row as RestaurantManager;
        return item.isPrimary ? (
          <span className="bg-accent-light text-accent text-xs font-medium px-2 py-1 rounded-full">
            Principal
          </span>
        ) : null;
      },
    },
    {
      key: "lastLoginAt",
      label: "Dernière connexion",
      nowrap: true,
      formatter: (_value: any, row: any) => {
        const item = row as RestaurantManager;
        return item.manager?.lastLoginAt
          ? formatDate(new Date(item.manager.lastLoginAt), true, true)
          : "Jamais";
      },
    },
    {
      key: "actions",
      label: "Actions",
      formatter: (_value: any, row: any) => {
        const item = row as RestaurantManager;
        if (item.isPrimary) return null;
        return (
          <div className="flex items-center gap-2">
            {canSetManagerPin && (
              <ButtonSetPin onClick={() => setPinTarget(item.manager)} />
            )}
            {canManage && (
              <ButtonDelete onClick={() => handleRemove(item)} />
            )}
          </div>
        );
      },
    },
  ];

  // The record id is already unique per row — don't clobber it with the
  // manager's user id, removal needs it.
  const tableData = data ?? [];

  return (
    <>
      <DataTable
        data={tableData}
        columns={columns}
        loading={isLoading}
        totalCount={tableData.length}
        searchable={false}
        emptyMessage={
          canManage
            ? "Ce restaurant n'a pas encore de manager. Ajoutez-en un avec « Ajouter un manager »."
            : "Aucun manager trouvé"
        }
      />
      {pinTarget && (
        <SetUserPinModal
          item={pinTarget}
          isOpen={true}
          onClose={() => setPinTarget(null)}
        />
      )}
    </>
  );
};

export default ListeRestaurantManagers;
